import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getStatsOverview } from "../api/adminApi";
import {
  StatCard, StatusBadge, BarList, Section, ShortId, fmtDate,
} from "../components/admin/AdminUI";

export default function AdminOverviewPage() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "stats", "overview"],
    queryFn: getStatsOverview,
    staleTime: 30_000,
  });

  if (isLoading) return <div className="text-zinc-500">Loading overview…</div>;
  if (isError)
    return (
      <div className="text-rose-400" data-testid="admin-overview-error">
        Error: {String(error?.message || error)}
      </div>
    );
  if (!data) return null;

  const counts = data.counts || {};
  const byImg = data.by_image_health || {};
  const byVert = data.by_vertical || {};
  const bySource = Object.entries(data.by_source || {})
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);
  const byCountry = Object.entries(data.by_country || {})
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);
  const totalActiveListings = counts.listings_active || 0;

  return (
    <div data-testid="admin-overview-page" className="space-y-8">
      <header>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
          /api/admin/stats/overview
        </span>
        <h1 className="text-2xl text-zinc-100 font-medium mt-1">
          Operational overview
        </h1>
      </header>

      {/* Top stat row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-overview-stats">
        <StatCard
          label="Users" value={counts.users}
          testid="overview-stat-users"
        />
        <StatCard
          label="Listings active" value={counts.listings_active}
          hint={`of ${counts.listings_total} total`}
          testid="overview-stat-listings-active"
        />
        <StatCard
          label="Imports total" value={counts.import_runs}
          hint={`${data.last_24h?.imports_run || 0} in last 24h`}
          testid="overview-stat-imports"
        />
        <StatCard
          label="Failed mappings" value={counts.failed_mappings}
          testid="overview-stat-failed"
        />
      </div>

      {/* Vertical + image health breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Section title="Vertical breakdown" testid="overview-by-vertical">
          <BarList
            items={[
              { label: "auto active", value: byVert.auto?.active || 0 },
              { label: "auto inactive", value: byVert.auto?.inactive || 0 },
              { label: "immo active", value: byVert.real_estate?.active || 0 },
              { label: "immo inactive", value: byVert.real_estate?.inactive || 0 },
              { label: "immo buy", value: byVert.real_estate?.buy || 0 },
              { label: "immo rent", value: byVert.real_estate?.rent || 0 },
            ]}
          />
        </Section>
        <Section title="Image health (all listings)" testid="overview-by-image-health">
          <BarList
            items={[
              { label: "alive", value: byImg.alive || 0 },
              { label: "dead", value: byImg.dead_on_check || 0 },
              { label: "unchecked", value: byImg.unchecked || 0 },
              { label: "no_image", value: byImg.no_image || 0 },
            ]}
            total={(byImg.alive || 0) + (byImg.dead_on_check || 0) + (byImg.unchecked || 0) + (byImg.no_image || 0)}
          />
        </Section>
      </div>

      {/* By source + by country */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Section title="By source platform" testid="overview-by-source">
          <BarList items={bySource} total={totalActiveListings} />
        </Section>
        <Section title="By country" testid="overview-by-country">
          <BarList items={byCountry} total={totalActiveListings} />
        </Section>
      </div>

      {/* Recent imports */}
      <Section title="Recent imports" testid="overview-recent-imports">
        {data.recent_imports?.length === 0 ? (
          <div className="text-xs text-zinc-500">No imports yet.</div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-zinc-950/50">
            <table className="w-full text-xs">
              <thead className="bg-zinc-900/60 text-zinc-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">ULID</th>
                  <th className="text-left px-3 py-2">Started</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Vertical</th>
                  <th className="text-right px-3 py-2">Received</th>
                  <th className="text-right px-3 py-2">Mapped OK</th>
                  <th className="text-right px-3 py-2">Mapped fail</th>
                  <th className="text-right px-3 py-2">Created</th>
                </tr>
              </thead>
              <tbody className="text-zinc-300">
                {data.recent_imports.map((r) => {
                  const c = r.counts || {};
                  return (
                    <tr key={r.ulid} className="border-t border-zinc-900/60 hover:bg-zinc-900/30">
                      <td className="px-3 py-2">
                        <Link
                          to={`/admin/imports/${r.ulid}`}
                          className="text-emerald-300 hover:underline"
                        >
                          <ShortId value={r.ulid} />
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-zinc-500">{fmtDate(r.started_at)}</td>
                      <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                      <td className="px-3 py-2 text-zinc-400">{r.vertical}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.received}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-300">{c.mapped_success}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-rose-300">{c.mapped_failed}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.listings_created}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <div className="text-[10px] text-zinc-600 font-mono">
        computed_at: {fmtDate(data.computed_at)}
      </div>
    </div>
  );
}
